/* istanbul ignore file */
/**
 * Created by rathompson on 11/5/21
 * File: ViewRolesModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useMemo, useContext, VFC, CSSProperties } from 'react'
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps
} from '@panwds/react-ui'
import StringTemplate from '../../../../../common/utils/StringTemplate'
import {
  LABEL_VIEW_INHERITED_TITLE,
  BUTTON_CANCEL, TITLE_RESULTING_PERMISSIONS
} from '../../../../constants/LabelConstants'
import GlobalContext from '../../../../../common/context/GlobalContext'
import type { IAM_Role } from '../../../../../common/reducers/types/IAMReducer.types'
import SelectedRolesTable from './SelectedRolesTable'
import AddPermissionsTable from '../../table/addPermissionsTable/AddPermissionsTable'

/**
 *   Role details modal
 */
const ViewRolesModal: VFC<ModalProps & {
  closeModal: () => void,
  app: any
}> = ({ closeModal, isOpen, app }) => {

  const { iamReducer } = useContext(GlobalContext)
  const { roleMap = {}, customRoleMap } = iamReducer

  const title = StringTemplate(LABEL_VIEW_INHERITED_TITLE, [app?.name])

  const aggregatedRoleMap = useMemo(() => {
    return { ...roleMap, ...customRoleMap }
  }, [roleMap, customRoleMap])

  const role: IAM_Role = useMemo(() => {

    const id = `${app.id}`.replace('-disabled', '')
    const app_id = id === 'all' ? '' : id

    const permissions = new Set()
    const permissionSets = new Map()
    const aggregatedPermissions = new Set()
    const explicitPermissions = new Set()
    const readOnlySets = new Map()
    const readWriteSets = new Map()

    if (Array.isArray(app.roles)) {
      for (const appRole of app.roles) {
        const { name } = appRole
        const role = aggregatedRoleMap[name]
        role?.permissions?.forEach(permission => {
          if (!permissions.has(permission) && permission.startsWith(app_id)) {
            permissions.add(permission)
          }
        })
        role?.permission_sets?.forEach(permissionSet => {
          if (!permissionSets.has(permissionSet.id) && permissionSet.id.startsWith(app_id)) {
            permissionSets.set(permissionSet.id, permissionSet)
          }
        })
        role?.aggregated_permissions?.forEach(permission => {
          if (!aggregatedPermissions.has(permission) && permission.startsWith(app_id)) {
            aggregatedPermissions.add(permission)
          }
        })
        role?.explicit_permissions?.forEach(permission => {
          if (!explicitPermissions.has(permission) && permission.startsWith(app_id)) {
            explicitPermissions.add(permission)
          }
        })
        role?.read_only_permission_sets?.forEach(permissionSet => {
          if (!readOnlySets.has(permissionSet.id) && permissionSet.id.startsWith(app_id)) {
            readOnlySets.set(permissionSet.id, permissionSet)
          }
        })
        role?.read_write_permission_sets?.forEach(permissionSet => {
          if (!readWriteSets.has(permissionSet.id) && permissionSet.id.startsWith(app_id)) {
            readWriteSets.set(permissionSet.id, permissionSet)
          }
        })
      }
    }
    return {
      id: 'role_aggregate',
      name: 'Role Aggregate',
      app_id,
      aggregated_permissions: [...aggregatedPermissions],
      permissions: [...permissions],
      permission_sets: Object.values(Object.fromEntries(permissionSets)),
      explicit_permissions: [...explicitPermissions],
      read_only_permission_sets: Object.values(Object.fromEntries(readOnlySets)),
      read_write_permission_sets: Object.values(Object.fromEntries(readWriteSets))
    } as any
  }, [app, aggregatedRoleMap])

  return (
    <Modal
      style={{ top: '50%', '--tw-translate-y': '-50%', zIndex: 1502 } as CSSProperties}
      size='xl'
      onClose={closeModal}
      isOpen={isOpen}>
      <div className='header-wrapper'>
        <ModalHeader title={title} />
      </div>
      <ModalBody addClassName='tw-max-h-full tw-overflow-hidden'>
        <SelectedRolesTable app={app} />
        <AddPermissionsTable
          title={TITLE_RESULTING_PERMISSIONS}
          role={role} setRole={() => null}
          setWebData={() => null}
          setAPIData={() => null}
          viewOnly
          useRoleData
          enableSearch
        />
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={closeModal}
          appearance="secondary"
        >{BUTTON_CANCEL}</Button>
      </ModalFooter>
    </Modal>
  )
}

ViewRolesModal.propTypes = {}
export default ViewRolesModal
